import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UntypedFormBuilder, Validators, UntypedFormControl, UntypedFormArray } from '@angular/forms';
import * as moment from 'moment';
import { UiService } from '@services/ui.service';
import { Order } from '@classes/order/order';
import { OrdersService } from '@services/orders.service';
import { AuthService } from '@services/auth.service';
import { RoleName } from '@enums/role-name.enum';
import { ConfirmValidMatcher } from '@classes/validations/confirm-valid-matcher';
import { Client } from '@classes/client';
import { ClientsService } from '@services/clients.service';
import { Site } from '@classes/site';
import { SitesService } from '@services/sites.service';
import { DictionaryValuesService } from '@services/dictionaryValues.service';
import { Address } from '@classes/address';
import { AddressesService } from '@services/addresses.service';
import { DictionaryValue } from '@classes/dictionary/dictionaryValue';
import { Observable } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatLegacyAutocomplete as MatAutocomplete, MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { map, startWith } from 'rxjs/operators';
import { LabileBlood } from '@enums/labile-blood.enum';
import { OrderType } from '@enums/order-type.enum';


@Component({
  selector: 'app-order-edit',
  templateUrl: './order-edit.component.html',
  styleUrls: ['./order-edit.component.scss']
})
export class OrderEditComponent extends FormEditBaseComponent implements OnInit {
  @Input() order: Order;

  clients: Client[];
  sites: Site[];
  transportTypes: DictionaryValue[];
  urgencyLevels: DictionaryValue[];
  senders: Address[];
  receivers: Address[];

  stringMaxLength = 50;
  commentMaxLength = 500;
  codeMaxLength = 20;
  isRestricted: boolean;
  isClient: boolean;
  cantEditName: boolean;
  mustCheckDate: boolean;
  confirmValidMatcher = new ConfirmValidMatcher();
  canEditTourPrices: boolean;
  canAddOtherAddress = true;

  invalidFields: string[] = [];
  invalidMessage: string = '';

  isFtso: boolean;

  transportTypesSelected: DictionaryValue[] = [];
  devices: DictionaryValue[];
  filteredTransportTypes: Observable<DictionaryValue[]>;
  transportTypesCtrl = new UntypedFormControl();
  selectable = true;
  removable = true;
  addOnBlur = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  @ViewChild('deviceInput') deviceInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  allTransportTypes: any;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private authSrv: AuthService,
    private clientsSrv: ClientsService,
    private ordersSrv: OrdersService,
    private sitesSrv: SitesService,
    private dictionarySrv: DictionaryValuesService,
    private addressesSrv: AddressesService,
  ) {
    super(uiSrv, fb);
  }

  ngOnInit(): void {
    super.ngOnInit();
    // tslint:disable-next-line: max-line-length
    this.isRestricted = this.authSrv.getUser().role.name !== RoleName.super_admin && this.authSrv.getUser().role.name !== RoleName.bizzdev;
    this.isFtso = this.authSrv.getUser().role.name === RoleName.super_admin || this.authSrv.getUser().role.name === RoleName.bizzdev || this.authSrv.getUser().role.name === RoleName.admin || this.authSrv.getUser().role.name === RoleName.admin_subcontractor || this.authSrv.getUser().role.name === RoleName.dispatcher;
    this.isClient = this.authSrv.getUser().role.name === RoleName.client;
    this.cantEditName = this.authSrv.getUser().role.name === RoleName.client || this.authSrv.getUser().role.name === RoleName.admin_client;

    this.canEditTourPrices =
      this.authSrv.getUser().role.name === RoleName.bizzdev
      || this.authSrv.getUser().role.name === RoleName.super_admin
      || this.authSrv.getUser().role.name === RoleName.admin
      || this.authSrv.getUser().role.name === RoleName.admin_subcontractor
      || this.authSrv.getUser().role.name === RoleName.admin_client
      || this.authSrv.getUser().role.name === RoleName.client;

    this.initSubscriptions();
  }

  isControlSet(control: string): boolean {
    return !!this.editForm.get(control);
  }

  onSubmit(): void {
    this.isLoading = true;
    this.update();
  }

  private update(): void {
    const formValues = this.editForm.value;

    const orderCopy: Order = JSON.parse(JSON.stringify(this.order));

    if (typeof formValues.sender[0].address === 'string') {
      formValues.sender[0].otherAddress = formValues.sender[0].address;
      formValues.sender[0].address = null;
    } else {
      formValues.sender[0].otherAddress = null;
    }

    if (this.isTour()) {
      formValues.sender[0].isDelivery = true;
      formValues.sender[0].isPickup = true;
    }

    formValues.receivers.map(receiver => {
      if (typeof receiver.address === 'string') {
        receiver.otherAddress = receiver.address;
        receiver.address = null;
      } else {
        receiver.otherAddress = null;
      }

      if (this.isTour()) {
        receiver.isDelivery = true;
        receiver.isPickup = true;
      }
      else {
        if (receiver.type === 'isDelivery') {
          receiver.isDelivery = true;
          receiver.isPickup = false;
        } else {
          receiver.isDelivery = false;
          receiver.isPickup = true;
        }
      }
    })

    if (formValues.patients.length > 0) {
      formValues.patients.map((patient, index) => {
        if (patient !== null) {
          if (patient.firstname === null && patient.lastname === null && patient.birthname === null && patient.birthdate === null && patient.productType === null && patient.quantity === null) {
            formValues.patients[index] = null;
          }
        }
      });
    }


    formValues.patients = formValues.patients.filter(patient => patient !== null);

    let callDate = null;

    if (formValues.callDate) {
      let callTime = formValues.callDate.split(':');
      let dateNow = moment(new Date());
      callDate = dateNow.hours(callTime[0]).minutes(callTime[1]).utc().toDate();
    }

    orderCopy.site = formValues.site;
    orderCopy.urgency = formValues.urgencyLevel;
    orderCopy.startDate = this.scheduleToDate(formValues.schedule[0]);
    orderCopy.ordererPhone = formValues.ordererPhone;
    orderCopy.ordererComment = formValues.ordererComment;
    orderCopy.ordererName = formValues.ordererName;
    orderCopy.sender = formValues.sender[0];
    orderCopy.receivers = formValues.receivers;
    orderCopy.transportTypes = this.transportTypesSelected;
    orderCopy.patients = formValues.patients;
    orderCopy.callDate = callDate;
    orderCopy.name = formValues.name;

    if (formValues.requestedDeliveryDate && formValues.requestedDeliveryTime) {
      const requestedDeliveryDate: moment.Moment = formValues.requestedDeliveryDate.local();
      const requestedDeliveryTime: number[] = formValues.requestedDeliveryTime.split(':').map(s => parseInt(s, 10));
      orderCopy.requestedDeliveryDate = requestedDeliveryDate.hours(requestedDeliveryTime[0]).minutes(requestedDeliveryTime[1]).utc().toDate();
    }


    if (this.isTour() && this.canEditTourPrices) {
      orderCopy.price = formValues.price;
      orderCopy.extraStepPrice = formValues.extraStepPrice;
    }

    if (this.isTour()) {
      orderCopy.code = formValues.code;
    }

    orderCopy.modifiedFields = this.checkWhatChanged(orderCopy);

    this.ordersSrv.put(orderCopy)
      .subscribe(() => {
        this.isLoading = false;
        this.onFinish();
        this.uiSrv.showSnackbar('orders.edit.edited', true);
      });
  }

  private initSubscriptions(): void {
    this.isLoading = true;

    const sitesPromise = new Promise((resolve, reject) => {
      this.sitesSrv.getNonTransmissionOnlyList()
        .subscribe(sitesFromSrv => {
          this.sites = sitesFromSrv;
          resolve(true);
        });
    });

    const clientsPromise = new Promise((resolve, reject) => {
      this.clientsSrv.getList()
        .subscribe(clientsFromSrv => {
          this.clients = clientsFromSrv;
          resolve(true);
        });
    });

    const typeTransportsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv.getTransportListBySite(this.order.site, this.order.client?.id)
        .subscribe(transportsFromSrv => {
          this.allTransportTypes = transportsFromSrv;
          resolve(true);
        });
    });

    const urgencyLevelsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv.getUrgencyListBySite(this.order.site, this.order.client?.id)
        .subscribe(urgencyLevelsFromSrv => {
          this.urgencyLevels = urgencyLevelsFromSrv;
          this.urgencyLevels.map(urgency => {
            if (this.order.urgency.id === urgency.id) {
              urgency.isChecked = true;
            }
          })
          resolve(true);
        });
    });

    const addressPromise = new Promise((resolve) => {
      this.addressesSrv.getListForSite(this.order.site.id)
        .subscribe(addressessFromSrv => {
          this.senders = addressessFromSrv.filter(address => address.isSender);
          this.receivers = addressessFromSrv.filter(address => address.isReceiver);


          resolve(true);
        });
    });

    Promise.all([sitesPromise, clientsPromise, typeTransportsPromise, urgencyLevelsPromise, addressPromise])
      .then(() => {
        this.isLoading = false;
        this.initForm();
      });
  }


  private initForm(): void {
    const fullSite = this.sites?.find((site) => site.id === this.order.site?.id);
    this.canAddOtherAddress = fullSite?.allowOtherAddress !== false;

    let callDate = null;
    if (this.order.callDate) {
      callDate = moment(this.order.callDate).hours().toString() + ':' + moment(this.order.callDate).minutes().toString();
    }

    let requestedDeliveryDate: moment.Moment = null;
    let requestedDeliveryTime: string = null;
    if (this.order.requestedDeliveryDate) {
      requestedDeliveryDate = moment(new Date(this.order.requestedDeliveryDate));
      requestedDeliveryTime = moment(requestedDeliveryDate).format('HH:mm');
    }

    this.editForm = this.fb.group({
      ordererComment: [this.order.ordererComment, [Validators.maxLength(this.commentMaxLength)]],
      // transportTypes: this.fb.array([]),
      urgencyLevel: new UntypedFormControl(null, [Validators.required]),
      schedule: this.fb.array([]),
      sender: this.fb.array([], Validators.required),
      receivers: this.fb.array([], Validators.required),
      ordererPhone: new UntypedFormControl(this.order.ordererPhone, [Validators.maxLength(this.stringMaxLength)]),
      ordererName: new UntypedFormControl(this.order.ordererName, [Validators.maxLength(this.stringMaxLength)]),
      patients: this.fb.array([]),
      site: new UntypedFormControl(this.order.site, [Validators.required]),
      callDate: new UntypedFormControl(callDate),
      name: new UntypedFormControl(this.order.name),
      requestedDeliveryDate: new UntypedFormControl(requestedDeliveryDate),
      requestedDeliveryTime: new UntypedFormControl(requestedDeliveryTime),
    });

    if (this.isTour() && this.canEditTourPrices) {
      this.editForm.addControl("price", new UntypedFormControl());
      this.editForm.addControl("extraStepPrice", new UntypedFormControl());
      this.getControl("price").setValue(this.order.price);
      this.getControl("extraStepPrice").setValue(this.order.extraStepPrice);
    }

    if (this.isTour()) {
      this.editForm.addControl("code", new UntypedFormControl(this.order.code, [Validators.maxLength(this.codeMaxLength)]));
    }

    this.sites.map(site => {
      if (site.id === this.order.site.id) {
        this.editForm.get('site').setValue(site);
      }
    })

    this.urgencyLevels.map(urgency => {
      if (urgency.isChecked) {
        this.editForm.get('urgencyLevel').setValue(urgency);
      }
    })

    this.filteredTransportTypes = this.transportTypesCtrl.valueChanges.pipe(
      startWith(null),
      map((fruit: string | null) => fruit ? this._filter(fruit) : this.allTransportTypes.slice()));

    if (this.order.transportTypes) {
      this.order.transportTypes.map(transportType => {
        this.transportTypesSelected.push(transportType);
      })

      this.checkIfSelected();
    }

    this.onAddSender(this.order.sender);

    if (this.order.receivers) {
      this.order.receivers.map((receiver, index) => {
        this.onAddReceiver(receiver, index);
      })
    } else {
      this.onAddReceiver();
    }

    this.initDateForm();

    this.editForm.valueChanges.subscribe(() => {
      this.invalidFields = this.findInvalidControls();
      this.invalidMessage = 'Le formulaire est incomplet';
      this.invalidFields.map(field => {
        this.invalidMessage = this.invalidMessage + ' ' + field;
      })
    })

  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.editForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(this.translateFields(name));
      }
    }


    //check si le dernier receiver est bien une livraison
    if (!this.isTour()) {
      let lastReceiver = this.editForm.value.receivers[this.editForm.value.receivers.length - 1];


      if (lastReceiver.type === 'isPickup') {
        invalid.push(' | La dernière étape est un enlèvement');
      }
    }

    if (this.transportTypesSelected.length === 0) {
      invalid.push(' | Il n\'y a aucun type de produit');
    }

    return invalid;
  }

  translateFields(name): string {
    if (name === 'site') {
      return ' | Donneur d\'ordre';
    }
    if (name === 'urgencyLevel') {
      return ' | Niveau d\'urgence';
    }
    if (name === 'sender') {
      return this.isTour() ? '| étape 1 incomplète' : ' | Lieu d\'enlèvement incomplet';
    }
    if (name === 'receivers') {
      return this.isTour() ? '| étape(s) incomplète(s)' : ' | Destinataire(s) incomplet(s)';
    }
    if (name === 'patients') {
      return ' | Patient(s) incomplet(s)';
    }
  }

  minTransportType(min = 1): any {
    return (formArray: UntypedFormArray) => {
      return formArray.controls.length >= min ? null : { required: true };
    };
  }

  // onAddTransportType(transportType?: DictionaryValue): void {

  //   this.getArray('transportTypes').push(this.fb.control(transportType));
  // }

  removeTransportType(transportTypeToRemove): void {
    this.getArray('transportTypes').controls.splice(this.getArray('transportTypes').controls.indexOf(transportTypeToRemove), 1);
    this.editForm.value.transportTypes.splice(this.editForm.value.transportTypes.indexOf(transportTypeToRemove.value), 1);
  }

  get transportTypesFields(): UntypedFormArray {
    return this.editForm.get('transportTypes') as UntypedFormArray;
  }

  private scheduleToDate(schedule: any): Date {
    if (!schedule.startDate || !schedule.startTime || typeof schedule.startDate === 'string') {
      return null;
    }

    const startDate: moment.Moment = schedule.startDate.local();
    const startTime: number[] = schedule.startTime.split(':').map(s => parseInt(s, 10));
    return startDate.hours(startTime[0]).minutes(startTime[1]).utc().toDate();
  }

  initDateForm(): void {
    const startMoment: moment.Moment = moment(new Date(this.order.startDate));
    const startTime: string = moment(startMoment).format('HH:mm');

    const newGroup = this.fb.group({
      startDate: [startMoment, Validators.required],
      startTime: [startTime, Validators.required]
    });

    this.getArray('schedule').push(newGroup);

    newGroup.controls.startDate.valueChanges
      .subscribe(() => {
        this.mustCheckDate = true;
      });

    newGroup.controls.startTime.valueChanges
      .subscribe(() => {
        this.mustCheckDate = true;
      });



  }

  resetStartDate(control) {
    control.get('startDate').reset();
  }

  resetBirthDate(control) {
    control.get('birthdate').reset();
  }

  onAddPatient(indexReceiver: number, patient?): void {
    let firstname = null;
    let birthname = null;
    let lastname = null;
    let birthdate = null;
    let productType = null;
    let quantity = null;
    let ipp = null;
    let id = null;

    if (patient) {
      firstname = patient.firstname;
      birthname = patient.birthname;
      lastname = patient.lastname;
      birthdate = patient.birthdate;
      productType = patient.productType;
      quantity = patient.quantity;
      ipp = patient.ipp;
      id = patient.id;
    }

    const newGroup = this.fb.group({
      firstname: [firstname, [Validators.maxLength(this.stringMaxLength)]],
      birthname: [birthname, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      lastname: [lastname, [Validators.maxLength(this.stringMaxLength)]],
      birthdate: [birthdate, [Validators.required]],
      productType: [productType, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      quantity: [quantity, [Validators.required]],
      ipp: [ipp, Validators.maxLength(this.stringMaxLength)],
      id: [id]
    });

    this.getArrayPatients(indexReceiver).push(newGroup);
  }

  onCopyPatient(indexReceiver: number, control) {
    let patient = control.value;

    const newGroup = this.fb.group({
      firstname: [patient.firstname, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      birthname: [patient.birthname, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      lastname: [patient.lastname, [Validators.maxLength(this.stringMaxLength)]],
      birthdate: [patient.birthdate, [Validators.required]],
      productType: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      quantity: [null, [Validators.required]],
      ipp: [null, Validators.maxLength(this.stringMaxLength)]
    });

    this.getArrayPatients(indexReceiver).push(newGroup);
  }

  getArrayPatients(index?: number) {
    let receivers = this.editForm.get('receivers') as UntypedFormArray;
    let patients = receivers.controls[index].get('patients') as UntypedFormArray;
    return patients;
  }

  onRemovePatient(indexReceiver: number, indexPatient: number): void {
    this.getArrayPatients(indexReceiver).removeAt(indexPatient);
  }

  isControlPatientSet(index: number): boolean {
    let receivers = this.editForm.get('receivers') as UntypedFormArray;

    return !!receivers.controls[index].get('patients');
  }

  onAddReceiver(receiver?, index?): void {
    let address = null;
    let otherAddress = null;
    let service = null;
    let ordererComment = null;
    let type = null;
    let id = null;

    if (receiver) {
      id = receiver.id;
      address = receiver.address;
      otherAddress = receiver.otherAddress;
      service = receiver.service;
      ordererComment = receiver.ordererComment;
      if (receiver.isPickup) {
        type = "isPickup";
      } else {
        type = "isDelivery";
      }
    }

    if (this.isTour()) {
      type = "isDelivery";
    }

    const newGroup = this.fb.group({
      id: [id],
      address: [address, [Validators.required]],
      otherAddress: [otherAddress],
      service: [service, [Validators.required]],
      ordererComment: [ordererComment, [Validators.maxLength(this.commentMaxLength)]],
      type: [type, [Validators.required]]
    });

    if (newGroup.get('type').value === 'isDelivery') {
      newGroup.addControl('patients', this.fb.array([]));
    }

    newGroup.get('type').valueChanges.subscribe(result => {
      if (result === 'isDelivery') {
        newGroup.addControl('patients', this.fb.array([]));
      } else {
        newGroup.removeControl('patients');
      }
    })

    if (receiver) {

      if (receiver.otherAddress) {
        newGroup.get('address').setValue(receiver.otherAddress);
        newGroup.get('otherAddress').setValue(receiver.otherAddress);
      }
      else {
        this.receivers.map(oneReceiver => {
          if (oneReceiver.id === receiver.address.id) {
            newGroup.get('address').setValue(oneReceiver);
          }
        })
      }
    }
    this.getArray('receivers').push(newGroup);

    if (receiver && receiver.patients) {
      receiver.patients.map(patient => {
        this.onAddPatient(index, patient);
      })
    }
  }

  onRemoveReceiver(index: number): void {
    this.getArray('receivers').removeAt(index);
  }

  onAddSender(sender?): void {
    let address = null;
    let otherAddress = null;
    let service = null;
    let ordererComment = null;
    let type = null;
    let id = null;

    if (sender) {
      id = sender.id;
      address = sender.address;
      otherAddress = sender.otherAddress;
      service = sender.service;
      ordererComment = sender.ordererComment;
    }

    const newGroup = this.fb.group({
      id: [id],
      address: [address, [Validators.required]],
      otherAddress: [otherAddress],
      service: [service, [Validators.required]],
      ordererComment: [ordererComment, [Validators.maxLength(this.commentMaxLength)]],
      isPickup: true,
      isDelivery: false
    });

    if (sender) {

      if (sender.otherAddress) {
        newGroup.get('address').setValue(sender.otherAddress);
        newGroup.get('otherAddress').setValue(sender.otherAddress);
      }
      else {
        this.senders.map(oneSender => {
          if (oneSender.id === sender.address.id) {
            newGroup.get('address').setValue(oneSender);
          }
        })
      }
    }
    this.getArray('sender').push(newGroup);
  }

  onCheckboxChange(e): any {
    const checkArray: UntypedFormArray = this.editForm.get('transportTypes') as UntypedFormArray;

    if (e.checked) {
      checkArray.push(new UntypedFormControl(e.source.value));
    } else {
      let i: number = 0;
      checkArray.controls.forEach((item: UntypedFormControl) => {
        if (item.value === e.source.value) {
          checkArray.removeAt(i);
          return;
        }
        i++;
      });
    }
  }

  onFinish(): void {
    this.editFinished.emit();
  }

  checkWhatChanged(orderCopy): string[] {
    let modifiedFields = [];

    if (this.isTour() && this.canEditTourPrices) {
      if (this.order.price !== orderCopy.price) {
        modifiedFields.push('Prix');
      }

      if (this.order.extraStepPrice !== orderCopy.extraStepPrice) {
        modifiedFields.push('Prix (étapes supplémentaires)');
      }
    }

    if (this.isTour() && this.order.code !== orderCopy.code) {
      modifiedFields.push('Code tournée');
    }

    if (this.order.site.id !== orderCopy.site.id) {
      modifiedFields.push('Donneur d\'ordre');
    }

    if (this.order.transportTypes.length !== orderCopy.transportTypes.length) {
      modifiedFields.push('Type de produit')
    } else {
      let hasChanged = false;
      orderCopy.transportTypes.map((transportType, index) => {
        if (this.order.transportTypes[index].id !== transportType.id) {
          hasChanged = true;
        }
      })

      if (hasChanged) {
        modifiedFields.push('Type de produit');
      }
    }

    if (this.order.urgency.id !== orderCopy.urgency.id) {
      modifiedFields.push('Niveau d\'urgence');
    }

    if (new Date(this.order.startDate).toString() !== new Date(orderCopy.startDate).toString()) {
      modifiedFields.push('Date, heure de départ souhaitée');
    }

    if (
      new Date(this.order.requestedDeliveryDate).toString() !==
      new Date(orderCopy.requestedDeliveryDate).toString()
    ) {
      modifiedFields.push('Dates, heures de livraison souhaitée');
    }

    if (this.order.sender.address) {
      if (orderCopy.sender.address) {
        if (this.order.sender.address.id !== orderCopy.sender.address.id) {
          modifiedFields.push('Adresse de l\'étape 1');
        }
      } else {
        modifiedFields.push('Adresse de l\'étape 1');
      }
    } else if (this.order.sender.otherAddress) {
      if (orderCopy.sender.otherAddress) {
        if (this.order.sender.otherAddress !== orderCopy.sender.otherAddress) {
          modifiedFields.push('Adresse de l\'étape 1');
        }
      } else {
        modifiedFields.push('Adresse de l\'étape 1');
      }
    }
    if (this.order.sender.service !== orderCopy.sender.service) {
      modifiedFields.push('Sevice de l\'étape 1')
    }

    if (this.order.sender.ordererComment ?? null !== orderCopy.sender.ordererComment ?? null) {
      modifiedFields.push('Commentaire de l\'étape 1');
    }

    if (this.order.ordererComment !== orderCopy.ordererComment) {
      modifiedFields.push('Commentaire sur la course');
    }

    if (this.order.ordererName !== orderCopy.ordererName) {
      modifiedFields.push('Nom du demandeur');
    }

    if (this.order.ordererPhone !== orderCopy.ordererPhone) {
      modifiedFields.push('Téléphone du demandeur');
    }

    if (this.order.receivers.length !== orderCopy.receivers.length) {
      modifiedFields.push('Étape(s)');
    } else {
      orderCopy.receivers.map((receiver, index) => {
        let oldReceiver = this.order.receivers[index];
        let position = index + 2;
        if (receiver.address) {
          if (oldReceiver.address) {
            if (receiver.address.id !== oldReceiver.address.id) {
              modifiedFields.push('Adresse de l\'étape ' + position);
            }

          } else {
            modifiedFields.push('Adresse de l\'étape ' + position);
          }
          if (receiver.service !== oldReceiver.service) {
            modifiedFields.push('Service de l\'étape ' + position);
          }
          if (receiver.ordererComment ?? null !== oldReceiver.ordererComment ?? null) {
            modifiedFields.push('Commentaire de l\'étape ' + position);
          }
        } else if (receiver.otherAddress) {
          if (oldReceiver.otherAddress) {
            if (receiver.otherAddress !== oldReceiver.otherAddress) {
              modifiedFields.push('Adresse de l\'étape ' + position);
            }

          } else {
            modifiedFields.push('Adresse de l\'étape ' + position);
          }
          if (receiver.service !== oldReceiver.service) {
            modifiedFields.push('Service de l\'étape ' + position);
          }
          if (receiver.ordererComment ?? null !== oldReceiver.ordererComment ?? null) {
            modifiedFields.push('Commentaire de l\'étape ' + position);
          }
        }
      })
    }

    if (this.order.patients.length !== orderCopy.patients.length) {
      modifiedFields.push('Liste patients');
    } else {
      orderCopy.patients.map((patient, index) => {
        let position = index + 1;
        if (patient.firstname != this.order.patients[index].firstname || patient.birthname != this.order.patients[index].birthname || patient.lastname != this.order.patients[index].lastname || new Date(patient.birthdate).toString() != new Date(this.order.patients[index].birthdate).toString() || patient.productType != this.order.patients[index].productType || patient.ipp != this.order.patients[index].ipp) {
          modifiedFields.push('Patient ' + position);
        }
      })
    }

    for (let i = 1; i < modifiedFields.length; i++) {
      modifiedFields[i] = " " + modifiedFields[i];
    }

    return modifiedFields;
  }

  remove(fruit: DictionaryValue): void {
    const index = this.transportTypesSelected.indexOf(fruit);

    if (index >= 0) {
      this.transportTypesSelected.splice(index, 1);
    }

    this.checkIfSelected();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    document.getElementById("deviceChipList").blur();
    if (!this.transportTypesSelected.includes(event.option.value)) {
      this.transportTypesSelected.push(event.option.value);
      // this.devices = this._filter(event.option.value);
      this.checkIfSelected();

      this.deviceInput.nativeElement.value = '';
    }

  }

  checkIfSelected() {

    this.filteredTransportTypes = this.filteredTransportTypes.pipe(
      map(array => {
        return array.map(element => {
          element.selected = false;
          this.transportTypesSelected.map(selected => {
            if (selected.id === element.id) {
              element.selected = true;
            }
          })
          return element;
        })

      })
    )
  }

  private _filter(value?): DictionaryValue[] {
    let filterValue = '';

    if (value.name) {
      filterValue = value.name.toLowerCase();
      return this.allTransportTypes.filter(el => {
        return el.id === value.id;
      });
    } else {
      filterValue = value.toLowerCase();
      return this.allTransportTypes.filter(transportType => transportType.name.toLowerCase().indexOf(filterValue) === 0);
    }




  }

  clearDevicesSelected(): void {
    this.transportTypesSelected = [];
    this.filteredTransportTypes.pipe(
      map(fruit => {
        return this.allTransportTypes;
      })
    )
    this.checkIfSelected();
  }

  showLabileText(labileBlood: LabileBlood): string {
    return 'labileBlood.' + LabileBlood[labileBlood];
  }

  public isTour(): boolean {
    if (!this.order) return false;

    return this.order.type == OrderType.tour;
  }

  isTypeOf(type: string, value: any): boolean {
    return typeof value === type;
  }
}
